import React, { ReactNode, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { GIButton, Modal } from 'app-components';

interface IErrorModal {
    titleId: string;
    errorText: string | ReactNode;
    onClose: () => void;
}

const StyledModal = styled.div`
    .gi-modal__content {
        width: 500px;
    }
    .gi-modal__body,
    .gi-modal__controls {
        display: flex;
        justify-content: center;
    }
`;

export const ErrorModal = (props: IErrorModal) => {
    const { titleId = 'ticketList.modal.errorModal.header.error', errorText, onClose } = props;
    const modalRef = useRef<HTMLDivElement>(null);
    const firstFocusableElementRef = useRef<HTMLElement | null>(null);
    const lastFocusableElementRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        const focusableSelectors = [
            'button:not([disabled])',
            'input:not([disabled])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            'a[href]',
        ].join(', ');

        const focusableElements = modalRef.current?.querySelectorAll(focusableSelectors);
        
        if (focusableElements && focusableElements.length > 0) {
            firstFocusableElementRef.current = focusableElements[0] as HTMLElement;
            lastFocusableElementRef.current = focusableElements[focusableElements.length - 1] as HTMLElement;
            
            firstFocusableElementRef.current?.focus();
        }

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Tab') {
                if (e.shiftKey) {
                    // Shift + Tab
                    if (document.activeElement === firstFocusableElementRef.current) {
                        e.preventDefault();
                        lastFocusableElementRef.current?.focus();
                    }
                } else {
                    // Tab
                    if (document.activeElement === lastFocusableElementRef.current) {
                        e.preventDefault();
                        firstFocusableElementRef.current?.focus();
                    }
                }
            }
            
            if (e.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose]);

    return (
        <StyledModal ref={modalRef}>
            <Modal id="errorModal">
                <Modal.Header id="errorModalHeader" titleId={titleId} onHide={onClose} />
                <Modal.Body>{errorText}</Modal.Body>
                <Modal.Controls>
                    <GIButton
                        id="errorModalNoButton"
                        onClickHandler={onClose}
                        variant="primary"
                        labelId="button.close"
                    />
                </Modal.Controls>
            </Modal>
        </StyledModal>
    );
};
